import { SearchOutlined } from '@ant-design/icons'
import { Button, Card, Input, Table, TableColumnsType, TablePaginationConfig } from 'antd'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import LinkAddress from 'component/link/link-address'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { formatDate } from 'utils/format-time'
import { logInfo } from 'utils/logger'

interface ReferralUserData {
  childrenAddress: string
  parentAddress: string
  createdAt: string
}

export default function ReferralUserData() {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ReferralUserData[]>([])
  const [total, setTotal] = useState<number>(0)
  const [current, setCurrent] = useState(DEFAULT_PAGE_INDEX)
  const [pageSize, setPageSize] = useState(10)
  const [address, setAddress] = useState<string>('')

  const getReferralUser = useCallback(
    async (page = DEFAULT_PAGE_INDEX, limit = DEFAULT_PAGE_SIZE, searchAddress = address) => {
      setLoading(true)
      // Clear data before fetching new data
      setData([])
      setTotal(0)

      try {
        const params: any = {
          page: page,
          limit,
        }

        if (searchAddress && searchAddress.trim() !== '') {
          params.ownerAddress = searchAddress
        }

        const res = await AppAdmin.getReferralUser(params)
        if (res?.data?.success) {
          const { results, totalResults } = res.data.data || {}

          setData(results || [])
          setTotal(totalResults || 0)
        }
      } catch (error) {
        setData([])
        setTotal(0)
        logInfo('Error: ', error)
      } finally {
        setLoading(false)
      }
    },
    [address]
  )

  const handleSearch = () => {
    setCurrent(DEFAULT_PAGE_INDEX)
    getReferralUser(DEFAULT_PAGE_INDEX, pageSize)
  }

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setCurrent(newPage || DEFAULT_PAGE_INDEX)
    setPageSize(newPageSize || DEFAULT_PAGE_SIZE)
    getReferralUser(newPage || DEFAULT_PAGE_INDEX, newPageSize || DEFAULT_PAGE_SIZE)
  }

  useEffect(() => {
    getReferralUser(current, pageSize, address)
  }, [address, current, getReferralUser, pageSize])

  const columns: TableColumnsType<ReferralUserData> = [
    {
      title: 'Children Address',
      dataIndex: 'childrenAddress',
      key: 'childrenAddress',
      render: (address: string) => <LinkAddress address={address} />,
    },
    {
      title: 'Parent Address',
      dataIndex: 'parentAddress',
      key: 'parentAddress',
      render: (address: string) => <LinkAddress address={address} />,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
  ]

  return (
    <Card style={{ margin: '0 auto' }}>
      <div style={{ marginBottom: '16px', display: 'flex', gap: '8px' }}>
        <Input
          placeholder="Enter wallet address"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onPressEnter={handleSearch}
          style={{ flex: 1 }}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch} loading={loading}>
          Search
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={total < 11 ? false : { total, current, pageSize }}
        onChange={handleTableChange}
        scroll={{ x: 1000 }}
        bordered
      />
    </Card>
  )
}
